import React from 'react';
import {Map, List} from 'immutable';
import type {AtomRenderProps} from '@packages/systems/wfdl';
import {withSystem} from '@packages/systems/wfdl';

import {
  TBoolean,
  TText,
  TList,
  TRecord,
  TNumber,
} from '@packages/systems/wfdl-ast';
import {createIntegratedAtom} from '@packages/utilities/integrated-types';

// these are the reserved url paths that will be requested on published sites.
// these paths hit a location block in webflow-proxy that tells it how to handle the request.
const RESERVED_PATHS = [
  'x9j3q8p7w6k2',
  'h7m2d9t5q1lz',
  's4k9f2v7n1yx',
  'e3t8h1p6z9rq',
  'v6c1m8n3k7hd',
  'z1r7q2t9x5lm',
  'n5g2h8j1k4vz',
  't2p9m6q1v7xh',
  'c8k1z6t3n9pd',
  'l7q2x9h5m1rv',
];

const ENCODED_RESERVED_PATHS = [
  'nvhc9u4gxsag',
  'g0lnomhfn3mg',
  'lsfr9no2cgfp',
  'v2645sy3qzdj',
  '0oarx4usxq70',
  'haqt6iy0yx2e',
  'wvxwa3jtwetc',
  '9i1h7htkfq16',
  'avljl2rk9q5p',
  'xa5e8du3i6yd',
];

type CombinedTag = {
  tagId: string;
  encodedTagId?: string;
  isUa: boolean;
  order: number;
  displayName: string;
};

/**
 * Encode a GA4/GTM ID using Base64 URL-safe encoding
 * @param {string} id - The GA4/GTM ID (e.g., "GT-123456" or "G-ABCD1234")
 * @returns {string} - Base64 URL-safe encoded string
 */
const encodeTagId = (id: string): string => {
  try {
    const encoder = new TextEncoder();
    const bytes = encoder.encode(id);

    let binary = '';
    for (let i = 0; i < bytes.length; i++) {
      binary += String.fromCharCode(bytes[i]!);
    }

    const base64 =
      typeof btoa === 'function'
        ? btoa(binary) // browser
        : Buffer.from(binary, 'binary').toString('base64'); // node fallback

    return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  } catch {
    return '';
  }
};

/**
 * Deterministically select a reserved path based on siteId
 * Uses SHA-256 hash of the siteId to ensure the same path is always selected
 * for the same siteId, both on server and client.
 *
 * Note: This function is synchronous and uses a simple hash for browser compatibility.
 * The server uses crypto.createHash('sha256'), but for consistency in path selection,
 * we use a deterministic hash function that produces similar distribution.
 *
 * @param {string} siteId - The site ID
 * @returns {string} - A reserved path from ENCODED_RESERVED_PATHS
 */
const getReservedPathForSite = (siteId: string): string => {
  // Use a deterministic hash function that matches server-side behavior
  // Server uses: crypto.createHash('sha256').update(siteId).digest()[0] % length
  // For browser compatibility, we use a simple hash that produces consistent results
  let hash = 0;
  for (let i = 0; i < siteId.length; i++) {
    const char = siteId.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash |= 0; // Convert to 32-bit integer
  }
  // Use absolute value and modulo to get index (matching server's hash[0] % length pattern)
  const index = Math.abs(hash) % ENCODED_RESERVED_PATHS.length;
  return ENCODED_RESERVED_PATHS[index]!;
};

// this concats the reserved path (defined above)with an encoded Google tag id
// this ensures that requests to the proxy can decode the Google tag id and route the request to the correct location.
const googleTagManagerReservedPath = (
  googleAnalyticsId: string,
  isGoogleTagGatewayXorBase64EncoderEnabled: boolean,
  siteId?: string
): string => {
  if (isGoogleTagGatewayXorBase64EncoderEnabled) {
    if (!siteId) {
      return '';
    }
    // Deterministically select a reserved path based on siteId
    // This ensures the same path is used on both server and client
    const reservedPath = getReservedPathForSite(siteId);
    const base64EncodedSiteId = encodeTagId(siteId);
    // The measurement path must consist of a reserved path (mapped in the proxy)
    // followed by the site ID in base64. The proxy will decode it and use it
    // to build the key that decrypts the Google Tag ID.
    return `/${reservedPath}${base64EncodedSiteId}/${googleAnalyticsId}`;
  } else {
    // For non-encoded paths, use random selection (legacy behavior)
    const paths = RESERVED_PATHS;
    const idx = Math.floor(Math.random() * paths.length);
    const reservedPath = paths[idx];
    const encodedTagId = encodeTagId(googleAnalyticsId);
    if (!encodedTagId) {
      return '';
    }
    return `/${reservedPath}${encodedTagId}`;
  }
};

// This is used as a way to handle multiple initializations of a Google Tags script.
// For example if a user includes this as custom code AND enables this feature from the dashboard,
// we want to ensure that the script is only initialized once.
// This "wig" script was provided by Google to support the Google Tag Gateway inititaive.
const googleTagsFirstPartyScript = (googleId: string) => (
  <script
    // eslint-disable-next-line react/no-danger
    dangerouslySetInnerHTML={{
      __html: `(function(w,i,g){w[g]=w[g]||[];if(typeof w[g].push=='function')w[g].push.apply(w[g],Array.isArray(i)?i:[i]);})(window,${googleId},'google_tags_first_party');`,
    }}
  />
);

const GoogleAnalytics = ({data, productId}: AtomRenderProps) => {
  // These google analytics IDs have been validated and escaped in the backend

  // When the Tag Gateway XOR+Base64 encoder is enabled, the server sends both
  // cleartext GA / GTM IDs and encoded variants. Cleartext values are used in
  // inline JS (wig + gtag('config')) so snippets stay readable and stable,
  // while the encoded values are only used when building Tag Gateway script
  // URLs so we can obfuscate the network paths without changing the inline code.
  // This is why we need both googleTagIds and encodedGoogleTagIds.

  const {
    // @ts-expect-error - TS2339 - property does not exist on type unknown
    googleAnalyticsId,
    // @ts-expect-error - TS2339 - property does not exist on type unknown
    googleAnonymizeIP,
    // @ts-expect-error - TS2339 - property does not exist on type unknown
    googleGtagEnabled,
    // @ts-expect-error - TS2339 - property does not exist on type unknown
    googleOptimizeId,
    // @ts-expect-error - TS2339 - property does not exist on type unknown
    googleUniversalAnalyticsId,
    // @ts-expect-error - TS2339 - property does not exist on type unknown
    googleAnalyticsFourId,
    // @ts-expect-error - TS2339 - property does not exist on type unknown
    encodedGoogleAnalyticsFourId,
    // @ts-expect-error - TS2339 - property does not exist on type unknown
    encodedGoogleUniversalAnalyticsId,
    // @ts-expect-error - TS2339 - property does not exist on type unknown
    isProductDetailPage,
    // @ts-expect-error - TS2339 - property does not exist on type unknown
    isGoogleTagGatewayEnabled,
    // @ts-expect-error - TS2339 - property does not exist on type unknown
    isMultipleGoogleTagIdsEnabled,
    // @ts-expect-error - TS2339 - property does not exist on type unknown
    isGoogleTagGatewayXorBase64EncoderEnabled,
    // @ts-expect-error - TS2339 - property does not exist on type unknown
    googleTagIds,
    // @ts-expect-error - TS2339 - property does not exist on type unknown
    encodedGoogleTagIds,
    // @ts-expect-error - TS2339 - property does not exist on type unknown
    siteId,
  } = data;

  const scripts: Array<
    | React.ReactElement<React.ComponentProps<'script'>>
    | React.ReactElement<React.ComponentProps<'style'>>
    | any
  > = [];
  const shouldUseMultipleMeasurementIds =
    isMultipleGoogleTagIdsEnabled && googleTagIds?.length > 0;

  // When GTG is on, migrate UA into the same multitag flow (one wig, GTG script URLs, gtag configs).
  // Combined list: multi-tag list or primary GA4 first, then UA last (if present).
  const shouldUseGtgMultitagBlock =
    isGoogleTagGatewayEnabled &&
    (Boolean(googleUniversalAnalyticsId) ||
      (googleTagIds && googleTagIds.length > 0) ||
      Boolean(googleAnalyticsFourId));

  const combinedTags: CombinedTag[] = [];
  if (shouldUseGtgMultitagBlock) {
    if (googleTagIds && googleTagIds.length > 0) {
      googleTagIds.forEach(
        (
          tag: {order: number; displayName: string; tagId: string},
          index: number
        ) => {
          const encodedTagId =
            isGoogleTagGatewayXorBase64EncoderEnabled &&
            Array.isArray(encodedGoogleTagIds)
              ? encodedGoogleTagIds[index]?.tagId
              : undefined;
          combinedTags.push({
            tagId: tag.tagId,
            encodedTagId,
            isUa: false,
            order: tag.order,
            displayName: tag.displayName,
          });
        }
      );
    } else if (googleAnalyticsFourId) {
      const encodedTagId =
        isGoogleTagGatewayXorBase64EncoderEnabled &&
        typeof encodedGoogleAnalyticsFourId === 'string' &&
        encodedGoogleAnalyticsFourId
          ? encodedGoogleAnalyticsFourId
          : undefined;
      combinedTags.push({
        tagId: googleAnalyticsFourId,
        encodedTagId,
        isUa: false,
        order: 0,
        displayName: 'GA4',
      });
    }
    // UA always last in tag order when GTG is enabled
    if (googleUniversalAnalyticsId) {
      const maxOrder =
        combinedTags.length > 0
          ? Math.max(...combinedTags.map((t) => t.order), -1) + 1
          : 0;
      combinedTags.push({
        tagId: googleUniversalAnalyticsId,
        encodedTagId: encodedGoogleUniversalAnalyticsId,
        isUa: true,
        order: maxOrder,
        displayName: 'Universal Analytics',
      });
    }
  }

  // Legacy Google Classic Analytics
  if (googleAnalyticsId) {
    const gaSnippet = [
      `var _gaq = _gaq || [];`,
      `_gaq.push(['_setAccount', '${googleAnalyticsId}'], ['_trackPageview']`,
      googleAnonymizeIP ? `['_gat._anonymizeIp']);` : `);`,
      `(function() {`,
      `  var ga = document.createElement('script');`,
      `  ga.src = ('https:' == document.location.protocol ? 'https://ssl' : 'http://www') + '.google-analytics.com/ga.js';`,
      `  var s = document.getElementsByTagName('script')[0]; s.parentNode.insertBefore(ga, s);`,
      `})();`,
    ].join('');
    scripts.push(
      <script
        type="text/javascript"
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{__html: gaSnippet}}
      />
    );
  }

  // Currently Universal Analytics tracking can be setup using either the gtag.js library (latest) https://developers.google.com/tag-platform/gtagjs/reference
  // or analytics.js library (legacy) https://developers.google.com/analytics/devguides/collection/analyticsjs
  // analytics.js library - skip when GTG is on (UA is migrated into GTG multitag)
  // this condition was implemented here: https://github.com/webflow/webflow/pull/23580
  if (
    googleUniversalAnalyticsId &&
    !googleGtagEnabled &&
    !isGoogleTagGatewayEnabled
  ) {
    const analyticsSnippet = [
      `(function(i,s,o,g,r,a,m){i['GoogleAnalyticsObject']=r;i[r]=i[r]||function(){`,
      `(i[r].q=i[r].q||[]).push(arguments)},i[r].l=1*new Date();a=s.createElement(o),`,
      `m=s.getElementsByTagName(o)[0];a.async=1;a.src=g;m.parentNode.insertBefore(a,m)`,
      `})(window,document,'script','https://www.google-analytics.com/analytics.js','ga');`,
      ``,
      `ga('create', '${googleUniversalAnalyticsId}', 'auto');`,
      googleOptimizeId ? `ga('require', '${googleOptimizeId}');` : '',
      googleAnonymizeIP ? `ga('set', 'anonymizeIp', true);` : '',
      `ga('send', 'pageview');`,
    ].join('');
    scripts.push(
      <script
        type="text/javascript"
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{__html: analyticsSnippet}}
      />
    );
  }

  // gtag.js library
  // this was implemented here: https://github.com/webflow/webflow/pull/23580
  // this condition is met when a user has a UA ID and has enabled gtag.js library (which used to be a toggle on the dashboard)
  // skip when UA is in the GTG multitag block (DEVPL-3754)
  if (
    googleUniversalAnalyticsId &&
    googleGtagEnabled &&
    !shouldUseMultipleMeasurementIds &&
    !shouldUseGtgMultitagBlock
  ) {
    const gtmReservedPath = isGoogleTagGatewayEnabled
      ? googleTagManagerReservedPath(
          googleUniversalAnalyticsId,
          isGoogleTagGatewayXorBase64EncoderEnabled,
          siteId
        )
      : '';

    // TODO: we can remove this in a future PR. Google Optimize is deprecated.
    const maybeOptimizeId = googleOptimizeId
      ? `, 'optimize_id': '${googleOptimizeId}'`
      : '';

    const GTAG_URL =
      isGoogleTagGatewayEnabled && gtmReservedPath
        ? gtmReservedPath
        : `https://www.googletagmanager.com/gtag/js?id=${googleUniversalAnalyticsId}`;
    const gtagSnippet = [
      `window.dataLayer = window.dataLayer || [];`,
      `function gtag(){dataLayer.push(arguments);}`,
      `gtag('js', new Date());`,
      // This automatically sends a pageview event
      // googleAnonymizeIP undefined === false
      `gtag('config', '${googleUniversalAnalyticsId}', {'anonymize_ip': ${String(
        googleAnonymizeIP || false
      )}${maybeOptimizeId}});`,
      // view_item is fired only for ecommerce product details pages
      isProductDetailPage && productId
        ? `gtag('event', 'view_item', {'items': [{'id': '${String(
            productId
          )}'}]});`
        : '',
    ].join('');

    scripts.push(<script async src={GTAG_URL} />);
    if (isGoogleTagGatewayEnabled) {
      scripts.push(
        googleTagsFirstPartyScript(`'${googleUniversalAnalyticsId}'`)
      );
    }
    scripts.push(
      <script
        type="text/javascript"
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{__html: gtagSnippet}}
      />
    );
  }

  // this is the latest version of Google Analytics/Google Tag Manager.
  // any new ID saved on the dashboard will be a GA4 ID.
  // skip when GA4 is in the GTG multitag block (DEVPL-3754)
  if (
    googleAnalyticsFourId &&
    !shouldUseMultipleMeasurementIds &&
    !shouldUseGtgMultitagBlock
  ) {
    // measurementIdForPath is the value we send to Tag Gateway. When the
    // XOR+Base64 encoder is enabled we prefer the pre-encoded ID from the
    // server; otherwise we fall back to the cleartext GA4 ID. Inline snippets
    // below always use googleAnalyticsFourId (cleartext).
    const measurementIdForPath =
      isGoogleTagGatewayXorBase64EncoderEnabled &&
      typeof encodedGoogleAnalyticsFourId === 'string' &&
      encodedGoogleAnalyticsFourId
        ? encodedGoogleAnalyticsFourId
        : googleAnalyticsFourId;

    const gtmReservedPath = isGoogleTagGatewayEnabled
      ? googleTagManagerReservedPath(
          measurementIdForPath,
          isGoogleTagGatewayXorBase64EncoderEnabled,
          siteId
        )
      : '';

    // TODO: we can remove this in a future PR. Google Optimize is deprecated.
    const maybeOptimizeId = googleOptimizeId
      ? `, {'optimize_id': '${googleOptimizeId}'}`
      : '';

    const GTAG_URL =
      isGoogleTagGatewayEnabled && gtmReservedPath
        ? gtmReservedPath
        : `https://www.googletagmanager.com/gtag/js?id=${googleAnalyticsFourId}`;
    const gtagSnippetParts: Array<string> = [
      `window.dataLayer = window.dataLayer || [];`,
      `function gtag(){dataLayer.push(arguments);}`,
      // used to distinguish client vs platform-level implementation and high-level Webflow-specific insights
      // required on the global and individual event level (https://developers.google.com/tag-platform/devguides/gtag-integration)
      `gtag('set', 'developer_id.dZGVlNj', true);`,
    ];

    if (isGoogleTagGatewayEnabled) {
      // recommended by the Google team for us to be able to associate Webflow sites with their suite of products.
      // multiple developer ids are supported, and can be used to track different aspects of the same site.
      gtagSnippetParts.push(`gtag('set', 'developer_id.dYWYxNW', true);`);
    }

    // This automatically sends a pageview event
    gtagSnippetParts.push(
      `gtag('js', new Date());`,
      `gtag('config', '${googleAnalyticsFourId}'${maybeOptimizeId});`,
      // view_item is fired only for ecommerce product details pages
      isProductDetailPage && productId
        ? `gtag('event', 'view_item', {'developer_id.dZGVlNj': true, 'items': [{'id': '${String(
            productId
          )}'}]});`
        : ''
    );

    const gtagSnippet = gtagSnippetParts.join('');

    scripts.push(<script async src={GTAG_URL} />);
    if (isGoogleTagGatewayEnabled) {
      scripts.push(googleTagsFirstPartyScript(`'${googleAnalyticsFourId}'`));
    }
    scripts.push(
      <script
        type="text/javascript"
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{__html: gtagSnippet}}
      />
    );
  }

  // GTG multitag block: UA + GA4/GTM tags via one wig and GTG script URLs (DEVPL-3754).
  if (shouldUseGtgMultitagBlock && combinedTags.length > 0) {
    scripts.push(
      googleTagsFirstPartyScript(
        `[${combinedTags.map((t) => `'${t.tagId}'`).join(',')}]`
      )
    );

    const gtagSnippetParts: Array<string> = [
      `window.dataLayer = window.dataLayer || [];`,
      `function gtag(){dataLayer.push(arguments);}`,
      `gtag('set', 'developer_id.dZGVlNj', true);`,
      `gtag('set', 'developer_id.dYWYxNW', true);`,
      `gtag('js', new Date());`,
    ];

    combinedTags.forEach((tag: CombinedTag) => {
      const tagIdForPath = tag.encodedTagId || tag.tagId;
      const gtmReservedPath = googleTagManagerReservedPath(
        tagIdForPath,
        isGoogleTagGatewayXorBase64EncoderEnabled,
        siteId
      );
      const GTAG_URL = gtmReservedPath
        ? gtmReservedPath
        : `https://www.googletagmanager.com/gtag/js?id=${tag.tagId}`;
      scripts.push(<script key={tag.tagId} async src={GTAG_URL} />);

      if (tag.isUa) {
        const maybeOptimizeId = googleOptimizeId
          ? `, 'optimize_id': '${googleOptimizeId}'`
          : '';
        gtagSnippetParts.push(
          `gtag('config', '${tag.tagId}', {'anonymize_ip': ${String(
            googleAnonymizeIP || false
          )}${maybeOptimizeId}});`
        );
      } else {
        gtagSnippetParts.push(`gtag('config', '${tag.tagId}');`);
      }
    });

    if (isProductDetailPage && productId) {
      gtagSnippetParts.push(
        `gtag('event', 'view_item', {'developer_id.dZGVlNj': true, 'items': [{'id': '${String(
          productId
        )}'}]});`
      );
    }

    const gtagSnippet = gtagSnippetParts.join('');
    // Content is server-built from template strings and atom data (tag IDs, productId); no raw user HTML.
    scripts.push(
      <script
        type="text/javascript"
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{__html: gtagSnippet}}
      />
    );
  }

  // This is used to support multiple Google Tag Manager IDs on the same site.
  // Skip when GTG multitag block is used (UA + tags are in the combined block).
  if (shouldUseMultipleMeasurementIds && !shouldUseGtgMultitagBlock) {
    scripts.push(
      googleTagsFirstPartyScript(
        `[${googleTagIds.map((tag: {tagId: string}) => `'${tag.tagId}'`).join(',')}]`
      )
    );

    const gtagSnippetParts: Array<string> = [
      `window.dataLayer = window.dataLayer || [];`,
      `function gtag(){dataLayer.push(arguments);}`,
      // used to distinguish client vs platform-level implementation and high-level Webflow-specific insights
      // required on the global and individual event level (https://developers.google.com/tag-platform/devguides/gtag-integration)
      `gtag('set', 'developer_id.dZGVlNj', true);`, // Always; unique to Webflow
      `gtag('set', 'developer_id.dYWYxNW', true);`, // Should always be true when GTG is enabled
      `gtag('js', new Date());`, // developer_ids should always be set before the 'js' event
    ];

    // For multiple tag IDs we follow the same pattern as the single GA4 case:
    // wig + gtag('config') always use tag.tagId (cleartext), and the optional
    // encodedGoogleTagIds array is only used to build Tag Gateway script src
    // URLs when the XOR+Base64 encoder flag is enabled.

    googleTagIds.forEach((tag: {tagId: string}, index: number) => {
      const encodedTagId =
        isGoogleTagGatewayXorBase64EncoderEnabled &&
        Array.isArray(encodedGoogleTagIds)
          ? encodedGoogleTagIds[index]?.tagId
          : undefined;

      const tagIdForPath = encodedTagId || tag.tagId;

      const GTAG_URL = googleTagManagerReservedPath(
        tagIdForPath,
        isGoogleTagGatewayXorBase64EncoderEnabled,
        siteId
      );

      // This automatically sends a pageview event
      gtagSnippetParts.push(`gtag('config', '${tag.tagId}');`);
      scripts.push(<script async src={GTAG_URL} />);
    });

    // view_item is fired only for ecommerce product details pages
    if (isProductDetailPage && productId) {
      gtagSnippetParts.push(
        `gtag('event', 'view_item', {'developer_id.dZGVlNj': true, 'items': [{'id': '${String(
          productId
        )}'}]});`
      );
    }

    const gtagSnippet = gtagSnippetParts.join('');
    scripts.push(
      <script
        type="text/javascript"
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{__html: gtagSnippet}}
      />
    );
  }

  return scripts.length ? scripts : null;
};

const staticBindings = Map({
  productId: List([
    Map({
      id: 'id',
      slug: 'id',
      type: 'PlainText',
    }),
  ]),
});

const config = createIntegratedAtom({
  showEmpty: false,
  // @ts-expect-error - TS2322 - Type '({ data, productId }: AtomRenderProps) => any[] | null' is not assignable to type 'AtomRender'.
  render: GoogleAnalytics,
  staticBindings,
  fields: {
    googleAnalyticsId: withSystem({private: true}, TText),
    googleUniversalAnalyticsId: withSystem({private: true}, TText),
    googleAnalyticsFourId: withSystem({private: true}, TText),
    encodedGoogleAnalyticsFourId: withSystem({private: true}, TText),
    encodedGoogleUniversalAnalyticsId: withSystem({private: true}, TText),
    googleGtagEnabled: withSystem({private: true}, TBoolean),
    googleOptimizeId: withSystem({private: true}, TText),
    googleAnonymizeIP: withSystem({private: true}, TBoolean),
    isProductDetailPage: withSystem({private: true}, TBoolean),
    isGoogleTagGatewayEnabled: withSystem({private: true}, TBoolean),
    isMultipleGoogleTagIdsEnabled: withSystem({private: true}, TBoolean),
    isGoogleTagGatewayXorBase64EncoderEnabled: withSystem(
      {private: true},
      TBoolean
    ),
    googleTagIds: withSystem(
      {private: true},
      TList(
        TRecord({
          order: TNumber,
          displayName: TText,
          tagId: TText,
        })
      )
    ),
    encodedGoogleTagIds: withSystem(
      {private: true},
      TList(
        TRecord({
          order: TNumber,
          displayName: TText,
          tagId: TText,
        })
      )
    ),
    siteId: withSystem({private: true}, TText),
  },
});

export default config;

export const test = {GoogleAnalytics} as const;
